/**
 * WHOSE SESSION THIS TAB IS.
 *
 * One value, read by the fetch wrapper on every request and by App when it
 * decides what to draw. Deliberately module state plus sessionStorage rather
 * than React state threaded through props: the value has to reach
 * `ApiClient.#send`, which is not a component and cannot read a hook, and the
 * whole point of Login As is that it is set ONCE rather than passed to every
 * call the way `?viewAs=` was to thirty of them.
 *
 * ---------------------------------------------------------------------------
 * sessionStorage, AND THE THREE PROPERTIES THAT MATTER
 *
 *   * it survives a RELOAD — which is the decision taken for this feature, and
 *     the opposite of what View As did. Acting as somebody is a deliberate act
 *     with a visible banner; losing it to a stray F5 halfway through checking
 *     what somebody sees is an annoyance with no safety benefit.
 *   * it dies with the TAB. A shared finance-floor desktop must not hand the
 *     next person a session belonging to somebody neither of them is. This is
 *     the same reason auth/msal.ts:58 puts tokens in sessionStorage and not
 *     localStorage.
 *   * it is per-tab, so an administrator can have themselves open in one tab
 *     and somebody else in another and compare them side by side — which is
 *     exactly how the thirteen differences were meant to be checked.
 *
 * IT IS NOT A PERMISSION, AND NOT TRUSTED. Editing this value in devtools
 * changes which oid is sent in a header. The server checks the real caller's
 * capability against the database on every request and refuses with 403 if they
 * may not act, or if the named person is not an active principal. Setting it to
 * somebody's id does not make you them; it makes you a 403.
 */

const KEY = 'mbi.actingAs'

/**
 * Storage can throw, not just come back empty: a private window, blocked site
 * data, or a sandboxed preview all reject the accessor outright. Every read and
 * write goes through these, so a browser that refuses storage loses the ability
 * to SURVIVE A RELOAD and nothing else — the in-memory value still works for
 * the life of the page.
 */
function read(): string | null {
  try {
    return window.sessionStorage.getItem(KEY)
  } catch {
    return null
  }
}

function write(value: string | null): void {
  try {
    if (value === null) window.sessionStorage.removeItem(KEY)
    else window.sessionStorage.setItem(KEY, value)
  } catch {
    /* in-memory only for this page; see read() */
  }
}

/**
 * Mirrored in memory so a storage failure does not silently drop the header
 * mid-session — which would serve the administrator their own data under a
 * banner naming somebody else, the one outcome this feature must never produce.
 */
let current: string | null = read()

/** The object id this tab is acting as, or null. */
export function actingAs(): string | null {
  return current
}

/**
 * Start or stop acting. Pass null to stop.
 *
 * The caller is responsible for re-reading identity afterwards: every cached
 * answer in the app belongs to the previous identity, and this function
 * deliberately does not reach into React to do that for you.
 */
export function setActingAs(objectId: string | null): void {
  current = objectId === null || objectId.trim() === '' ? null : objectId.trim()
  write(current)
}

/** The request header the server reads. Exported so the client and tests agree on it. */
export const ACT_AS_HEADER = 'X-Act-As'

/** The response header the server echoes, naming whose session it actually served. */
export const ACTING_AS_HEADER = 'X-Acting-As'
